import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, LayoutDashboard, Menu, X, ClipboardList } from 'lucide-react';
import { authAPI } from '../api';

const LINKS = [
  { to: '/',        label: 'Home'    },
  { to: '/about',   label: 'About'   },
  { to: '/order',   label: 'Order'   },
  { to: '/apply',   label: 'Apply'   },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser]         = useState(null);
  const [open, setOpen]         = useState(false);
  const location  = useLocation();
  const navigate  = useNavigate();
  const token     = localStorage.getItem('bce_token');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (token) {
      authAPI.getUserProfile()
        .then(r => setUser(r.data))
        .catch(() => localStorage.removeItem('bce_token'));
    }
  }, [token]);

  const handleLogin = async () => {
    try {
      const { data } = await authAPI.getDiscordAuthUrl();
      window.location.href = data.url;
    } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('bce_token');
    setUser(null);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const linkStyle = (path) => ({
    padding: '6px 14px',
    fontFamily: 'var(--font-condensed)',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: isActive(path) ? 'var(--bce-gold)' : 'var(--bce-text)',
    borderBottom: isActive(path) ? '2px solid var(--bce-gold)' : '2px solid transparent',
    transition: 'color 0.15s, border-color 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0,
      height: 'var(--header-h)',
      zIndex: 500,
      background: scrolled ? 'rgba(8,8,8,0.97)' : 'rgba(8,8,8,0.6)',
      borderBottom: `1px solid ${scrolled ? 'rgba(245,168,0,0.2)' : 'rgba(255,255,255,0.04)'}`,
      backdropFilter: 'blur(12px)',
      transition: 'background 0.3s, border-color 0.3s',
    }}>
      <div style={{
        height: '100%',
        display: 'flex', alignItems: 'center',
        padding: '0 2rem', gap: '1.5rem',
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <img src="/logo.png" alt="Big Country Energy"
            style={{ height: 42, width: 'auto' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div style={{ display: 'none', alignItems: 'baseline', gap: '6px' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--bce-white)', letterSpacing: '0.04em' }}>BIG COUNTRY</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--bce-gold)', letterSpacing: '0.04em' }}>ENERGY</span>
          </div>
        </Link>

        {/* Divider */}
        <div style={{ width: 2, height: 28, background: 'var(--bce-gold)', opacity: 0.4, flexShrink: 0 }} />

        {/* Desktop nav */}
        <nav className="bce-nav-desktop" style={{ display: 'flex', gap: '0.25rem', flex: 1 }}>
          {LINKS.map(l => (
            <Link key={l.to} to={l.to} style={linkStyle(l.to)}
              onMouseEnter={e => { if (!isActive(l.to)) e.currentTarget.style.color = 'var(--bce-white)'; }}
              onMouseLeave={e => { if (!isActive(l.to)) e.currentTarget.style.color = 'var(--bce-text)'; }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Desktop auth */}
        <div className="bce-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {!token ? (
            <button className="bce-btn bce-btn-gold bce-btn-sm" onClick={handleLogin}>
              Login with Discord
            </button>
          ) : (
            <>
              {user?.is_management && (
                <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => navigate('/dashboard')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <LayoutDashboard size={13} /> Dashboard
                </button>
              )}
              <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => navigate('/my-orders')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ClipboardList size={13} /> My Orders
              </button>
              {user?.avatar_url && (
                <img src={user.avatar_url} alt={user.username}
                  style={{ width: 32, height: 32, border: '2px solid var(--bce-gold)', objectFit: 'cover' }} />
              )}
              <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={handleLogout}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <LogOut size={13} />
              </button>
            </>
          )}
        </div>

        {/* Mobile: auth + hamburger */}
        <div className="bce-nav-mobile" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {!token ? (
            <button className="bce-btn bce-btn-gold bce-btn-sm" onClick={handleLogin}
              style={{ fontSize: 11, padding: '6px 12px' }}>
              Login
            </button>
          ) : (
            user?.avatar_url && (
              <img src={user.avatar_url} alt="" style={{ width: 28, height: 28, border: '1px solid var(--bce-gold)', objectFit: 'cover' }} />
            )
          )}
          <button onClick={() => setOpen(o => !o)}
            style={{ background: 'none', border: '1px solid rgba(245,168,0,0.25)', padding: '6px 8px', color: 'var(--bce-white)', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              background: 'rgba(6,6,6,0.98)',
              borderTop: '1px solid rgba(245,168,0,0.12)',
              borderBottom: '2px solid var(--bce-gold)',
            }}
          >
            <nav style={{ display: 'flex', flexDirection: 'column' }}>
              {LINKS.map(l => (
                <Link key={l.to} to={l.to}
                  style={{
                    padding: '1rem 2rem',
                    fontFamily: 'var(--font-condensed)',
                    fontSize: 13, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: isActive(l.to) ? 'var(--bce-gold)' : 'var(--bce-text)',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  {l.label}
                  {isActive(l.to) && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--bce-gold)' }} />}
                </Link>
              ))}
              {token && (
                <Link to="/my-orders"
                  style={{
                    padding: '1rem 2rem',
                    fontFamily: 'var(--font-condensed)',
                    fontSize: 13, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: isActive('/my-orders') ? 'var(--bce-gold)' : 'var(--bce-text)',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <ClipboardList size={14} /> My Orders
                </Link>
              )}
              {token && user?.is_management && (
                <Link to="/dashboard"
                  style={{
                    padding: '1rem 2rem',
                    fontFamily: 'var(--font-condensed)',
                    fontSize: 13, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: isActive('/dashboard') ? 'var(--bce-gold)' : 'var(--bce-text)',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <LayoutDashboard size={14} /> Dashboard
                </Link>
              )}
              {token && (
                <button onClick={handleLogout}
                  style={{
                    margin: '0.75rem 2rem 1rem',
                    background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                    color: 'var(--bce-text-faint)',
                    fontFamily: 'var(--font-condensed)', fontSize: 11,
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    padding: '8px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6, cursor: 'pointer',
                  }}
                >
                  <LogOut size={13} /> Logout
                </button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
